import React from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';

import RadioGroupForm from '../../../../../components/form/RadioGroupForm';
import InputForm from '../../../../../components/form/InputForm';
import { asyncSimpleModal, setDash } from '../../../../../lib/common';
import { formRequiredRules } from '../../../../../lib/formRules';
import { REX_CATEGORY_NAME, REX_CATEGORY_SUB_TEXT } from '../../../../../lib/rex';
import { INVALID_CATEGORY_SUB_TEXT_MESSAGE } from '../../../../../constants/service/invalidMsg/categoryInvalidMsg';
import {
  BASE_CATEGORY_ID_ARRAY,
  CATEGORY_BANNER_IMG_HEIGHT,
  CATEGORY_BANNER_IMG_WIDTH,
  CATEGORY_EXHIBIT_TYPE,
  CATEGORY_EXHIBIT_TYPE_TEXT,
  CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST,
  CATEGORY_MEMO_TEXT_MAX_LENGTH,
  CATEGORY_SORT_TYPE_SELECT_LIST,
  CATEGORY_STATUS_TYPE,
  CATEGORY_STATUS_TYPE_SELECT_LIST,
  CATEGORY_SUB_TEXT_MAX_LENGTH,
  CATEGORY_TYPE,
  EMPHASIS_CATEGORY_NAME_MAX_LENGTH,
  GENERAL_CATEGORY_NAME_MAX_LENGTH,
} from '../../../../../constants/service/app/categoryConstants';
import './MainCategoryInfo.scss';
import { Category } from '../../types';
import CategoryForm, { Content, Label, Row } from '../../../common/CategoryForm';
import ImgUploadForm from '../../../../../components/form/ImgUploadForm';
import { REQUIRED_MSG } from '../../../../../constants/service/common/formRulesConstants';
import { IMAGE_SIZE_GUIDE_TEXT, IMAGE_TYPE_GUIDE_TEXT } from '../../../../../constants/service/common/commonConstants';

interface IMainCategoryInfoProps {
  form: WrappedFormUtils;
  isEditable: boolean;
  category: Category;
}

const MainCategoryInfo = (props: IMainCategoryInfoProps) => {
  const { form, isEditable, category } = props;

  return (
    <CategoryForm className="main-category-info-wrap">
      <Row>
        <Label text="카테고리 종류" />
        <Content>
          <div className="base-form">{category.type === CATEGORY_TYPE.GENERAL ? '일반 카테고리' : '강조 카테고리'}</div>
        </Content>
        <Label text="카테고리 구분" />
        <Content>
          <div className="base-form">1차 카테고리</div>
        </Content>
      </Row>
      <Row>
        <Label
          text="카테고리 명"
          required
          withCount={isEditable}
          content={form.getFieldValue('name')}
          maxLength={
            category.type === CATEGORY_TYPE.EMPHASIS
              ? EMPHASIS_CATEGORY_NAME_MAX_LENGTH
              : GENERAL_CATEGORY_NAME_MAX_LENGTH
          }
        />
        <Content>
          <InputForm
            className="category-name"
            form={form}
            name="name"
            initialValue={category.name || null}
            isEditable={isEditable}
            rules={[
              formRequiredRules(true, '카테고리명을 입력해주세요'),
              { pattern: REX_CATEGORY_NAME, message: '한글, 영어, 특수문자만 입력 가능합니다' },
            ]}
            maxLength={
              category.type === CATEGORY_TYPE.EMPHASIS
                ? EMPHASIS_CATEGORY_NAME_MAX_LENGTH
                : GENERAL_CATEGORY_NAME_MAX_LENGTH
            }
          />
        </Content>
        <Label text="카테고리 ID" />
        <Content>
          <div className="base-form">{category.id}</div>
        </Content>
      </Row>
      <Row>
        <Label text="카테고리 활성 여부" required />
        <Content>
          <RadioGroupForm
            className="category-status"
            form={form}
            name="status"
            rules={[formRequiredRules(true, '필수 정보 입력해주세요', false)]}
            list={CATEGORY_STATUS_TYPE_SELECT_LIST}
            initialValue={category.status}
            onChange={async (value: any) => {
              // 카테고리 활성 여부를 비활성으로 바꿀 때 확인 모달 노출
              if (value === CATEGORY_STATUS_TYPE.INACTIVE) {
                // antd의 onChange는 비동기적으로 동작하므로 setTimeout으로 처리하여 활성 여부 모달 창에서 결과가 나오기 전까지 활성 상태 유지
                setTimeout(() => {
                  form.setFieldsValue({ status: CATEGORY_STATUS_TYPE.ACTIVE });
                }, 0);

                const result = await asyncSimpleModal({
                  type: 'confirm',
                  title: '카테고리 활성 여부 변경',
                  message:
                    '1차 카테고리의 활성 여부를 비활성으로 변경 시 2차 카테고리의 모든 정보를 수정 하실 수 없습니다.\n\n 1. 2차 카테고리 신규 생성 정보 및 수정 내용 초기화\n 2. 2차 카테고리 활성 여부 모두 비노출 상태로 변경\n\n 변경 하시겠습니까?',
                });

                // 취소 버튼 클릭 시 비활성 상태로 바꾸지 않음
                if (!result) return;

                // 확인 버튼 클릭 시, 비활성 상태로 바꿈
                form.setFieldsValue({ status: CATEGORY_STATUS_TYPE.INACTIVE });
              }
            }}
            disabled={!isEditable || BASE_CATEGORY_ID_ARRAY.includes(category.id)}
          />
        </Content>
        <Label text="카테고리 전시 여부" />
        <Content>
          <div className="base-form">
            {category.isExhibit === CATEGORY_EXHIBIT_TYPE.EXHIBIT
              ? CATEGORY_EXHIBIT_TYPE_TEXT[CATEGORY_EXHIBIT_TYPE.EXHIBIT]
              : CATEGORY_EXHIBIT_TYPE_TEXT[CATEGORY_EXHIBIT_TYPE.INEXHIBIT]}
          </div>
        </Content>
      </Row>
      <Row>
        <Label text="카테고리 생성 사유" />
        <Content>
          {isEditable ? (
            <InputForm
              className="category-memo"
              form={form}
              name="memo"
              rules={[{ pattern: REX_CATEGORY_NAME, message: '한글, 영어, 특수문자만 입력 가능합니다' }]}
              initialValue={category.memo || null}
              maxLength={CATEGORY_MEMO_TEXT_MAX_LENGTH}
            />
          ) : (
            <div className="base-form">{setDash(category.memo)}</div>
          )}
        </Content>
      </Row>
      <Row>
        <Label
          text="노출 위치"
          required
          tooltip={
            <>
              <div>
                • <strong>공통</strong>은 카테고리탭, 검색결과, 브랜드상세에서 노출됩니다.
              </div>
              <div>
                • <strong>로컬</strong>은 카테고리탭에서만 노출됩니다.
              </div>
            </>
          }
        />
        <Content>
          <RadioGroupForm
            className="category-exposure-type"
            form={form}
            name="exposureType"
            rules={[formRequiredRules(true, '필수 정보 입력해주세요', false)]}
            list={CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST}
            initialValue={category.exposureType}
            disabled
          />
        </Content>
      </Row>
      <Row>
        <Label text="상품 정렬순" required />
        <Content>
          <RadioGroupForm
            className="category-sort-type"
            form={form}
            name="sortType"
            rules={[formRequiredRules(true, '필수 정보 입력해주세요', false)]}
            list={CATEGORY_SORT_TYPE_SELECT_LIST}
            initialValue={category.sortType}
            disabled={!isEditable}
          />
        </Content>
      </Row>
      <Row>
        <Label text="하위 카테고리" />
        <Content>
          <div className="base-form">{category.subCategoryCount}</div>
        </Content>
        <Label text="등록 상품 수" />
        <Content>
          <div className="base-form">{category.productCount}</div>
        </Content>
      </Row>
      {category.type === CATEGORY_TYPE.EMPHASIS ? (
        <Row>
          <Label text="이미지" required />
          <Content>
            <ImgUploadForm
              form={form}
              isEditable={isEditable}
              initialValue={[...category.categoryAttachments]}
              rules={[formRequiredRules(true, REQUIRED_MSG, true, 'array')]}
              minCnt={1}
              modalTitle="이미지"
              cropMode={false}
              capacityLimit={1}
              name="categoryAttachments"
              targetSize={{
                width: CATEGORY_BANNER_IMG_WIDTH,
                height: CATEGORY_BANNER_IMG_HEIGHT,
              }}
              restriction={{
                square: true,
                widthMin: CATEGORY_BANNER_IMG_WIDTH,
                heightMin: CATEGORY_BANNER_IMG_WIDTH,
              }}
              subContent={
                <div>
                  *비율 가로1: 세로1
                  <br />
                  *권장 크기: 216X216 px(권장 크기보다 작을 경우, 앱에서 화질 저하의 우려가 있습니다.)
                  <br />*{IMAGE_SIZE_GUIDE_TEXT}
                  <br />*{IMAGE_TYPE_GUIDE_TEXT}
                </div>
              }
            />
          </Content>
        </Row>
      ) : null}
    </CategoryForm>
  );
};

export default MainCategoryInfo;
